import { Component, ChangeDetectionStrategy, ElementRef, ViewChild, AfterViewChecked, OnInit, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { EditorModule } from 'primeng/editor';
import { DialogModule } from 'primeng/dialog';
import { SidebarModule } from 'primeng/sidebar';
import { TabViewModule } from 'primeng/tabview';
import { PickerComponent } from '@ctrl/ngx-emoji-mart';
import { ChatStateService } from '../../../services/chat/chat-state.service';
import { ChatService } from '../../../services/chat/chat.service';
import { ConversationResponse } from '../../../models/chat/chat-api.models';
import { ChatMessageResponse, ChatMessageTypeEnum, ChatMediaResponse } from '../../../models/chat/chat-api.models';
import { Observable, combineLatest, map, Subscription, BehaviorSubject } from 'rxjs';
import { MessageService } from 'primeng/api';
import { AuthService } from '../../../services/account/auth.service';
import { UserStateModel } from '../../../states/user/user-state-model';
import { formatDistanceToNow } from 'date-fns';

@Component({
  selector: 'app-chat-window',
  standalone: true,
  imports: [CommonModule, FormsModule, AvatarModule, ButtonModule, EditorModule, DialogModule, SidebarModule, TabViewModule, PickerComponent],
  templateUrl: './chat-window.component.html',
  styleUrls: ['./chat-window.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [MessageService] // Provide locally if not global
})
export class ChatWindowComponent implements OnInit, AfterViewChecked, OnDestroy {
  @ViewChild('scrollContainer') scrollContainer!: ElementRef;
  
  activeConv$ = this.state.activeConversation$;
  messages$ = this.state.messages$;
  pinnedMessages$ = this.state.pinnedMessages$;
  typingText$: Observable<string | null>;
  
  newMessage = '';
  showEmoji = false;
  showMobileActions = false;
  showAttachMenu = false;
  showToolbar = false;

  // Attachment State
  selectedFile: File | null = null;
  filePreviewUrl: string | null = null;
  selectedMessageType: ChatMessageTypeEnum = ChatMessageTypeEnum.Text;

  // Image Viewer State
  viewingImage: string | null = null;
  
  // Download Confirmation State
  displayDownloadConfirm = false;
  confirmDownloadTarget: { url: string, name: string } | null = null;

  // Media Drawer State
  showMediaDrawer = false;
  mediaList: ChatMediaResponse[] = [];
  mediaType: 'Image' | 'File' | 'Link' | 'All' = 'File';
  isMediaLoading = false;
  isLoadingMoreMedia = false;
  hasMoreMedia = true;
  mediaSkip = 0;
  mediaTake = 10;

  // Pinned Messages Drawer State
  showPinnedDrawer = false;
  // Scroll State
  private shouldScrollToBottom = false;
  private previousMessageCount = 0;

  // Current User State
  currentUser$: Observable<UserStateModel | null>;
  currentUserId: string | null = null;
  private _currentUser = new BehaviorSubject<UserStateModel | null>(null);

  private typingTimeout: any;
  private isTyping = false;
  private currentConv: ConversationResponse | null = null;
  private convSub!: Subscription;

  constructor(
      private state: ChatStateService,
      private chatService: ChatService,
      private cdr: ChangeDetectorRef,
      private messageService: MessageService,
      private authService: AuthService
  ) {
      this.currentUser$ = this._currentUser.asObservable();
      // Logic for typing text
      this.typingText$ = combineLatest([this.state.typingUsers$, this.state.activeConversation$]).pipe(
          map(([map, conv]) => {
              if (!conv || !map.has(conv.id)) return null;
              const set = map.get(conv.id)!;
              if (set.size > 0) return 'Typing...'; // Could resolve names
              return null;
          })
      );
  }

  // ... (ngOnInit, ngOnDestroy, etc. remain the same) ...
  ngOnInit() {
      // Fetch current user Details from localStorage
      try {
          const accountStr = localStorage.getItem('account');
          if (accountStr) {
              const account = JSON.parse(accountStr);
              this.currentUserId = account.id || account.Id || account.userId || account.UserId || null;
              this._currentUser.next(account as UserStateModel);
          }
      } catch(e) {
          console.error('Error parsing account from localStorage', e);
      }

      this.convSub = this.state.activeConversation$.subscribe(c => {
          this.currentConv = c;
          this.shouldScrollToBottom = true; // Scroll on conversation change
          this.previousMessageCount = 0; // Reset count
      });
      
      // Listen to messages change to know when to scroll
      this.state.messages$.subscribe((messages) => {
          // Scroll if the number of messages increased (new message received/sent)
          if (messages.length > this.previousMessageCount) {
              this.shouldScrollToBottom = true;
          } 
          // Also scroll to bottom if we are ALREADY at the bottom and a message's content/pin state changed
          else if (this.isUserNearBottom()) {
              this.shouldScrollToBottom = true;
          }
          this.previousMessageCount = messages.length;
      });
  }

  ngOnDestroy() {
      if (this.convSub) this.convSub.unsubscribe();
      this.clearTypingState();
  }

  ngAfterViewChecked() {
      if (this.shouldScrollToBottom) {
          this.scrollToBottom();
          this.shouldScrollToBottom = false;
      }
  }

  scrollToBottom() {
      setTimeout(() => {
          try {
              if (this.scrollContainer) {
                  this.scrollContainer.nativeElement.scrollTop = this.scrollContainer.nativeElement.scrollHeight;
              }
          } catch(e) {}
      }, 100);
  }

  // Check if the scroll position is at the bottom of the container (with a 50px threshold buffer)
  isUserNearBottom(): boolean {
      if (!this.scrollContainer) return true; // Default to true if container isn't rendered yet
      const el = this.scrollContainer.nativeElement;
      if (el.scrollHeight <= el.clientHeight) return true;
      return Math.abs(el.scrollHeight - el.scrollTop - el.clientHeight) < 50;
  }

  scrollToMessage(messageId: number) {
      if (!this.scrollContainer) return;
      
      const element = document.getElementById(`message-${messageId}`);
      if (element) {
          // Scroll into view smooth
          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
          
          // Add a temporary highlight class
          element.classList.add('highlight-message');
          setTimeout(() => {
              element.classList.remove('highlight-message');
          }, 2000); // Remove after 2 seconds
          
          // Close the drawer so the user can see the message
          this.showPinnedDrawer = false;
      }
  }

  trackById(index: number, msg: ChatMessageResponse) {
      return msg.id;
  }
  
  send() {
      // Strip HTML if it's just empty paragraphs
      const msgContent = this.newMessage || '';
      const plainText = msgContent.replace(/<[^>]*>/g, '').trim();
      const hasContent = plainText || msgContent.includes('<img');

      if (!hasContent && !this.selectedFile) {
          this.newMessage = '';
          return;
      }

      if (this.selectedFile) {
          this.state.sendMessage(msgContent, this.selectedFile, this.selectedMessageType);
          this.clearAttachment();
      } else {
          // Detect if the message contains a link
          const urlRegex = /(https?:\/\/[^\s]+|www\.[^\s]+)/i;
          const hasLink = msgContent.includes('<a href=') || urlRegex.test(plainText);
          const messageType = hasLink ? ChatMessageTypeEnum.Link : ChatMessageTypeEnum.Text;
          
          // Cast null to undefined because ChatStateService expects file?: File
          this.state.sendMessage(msgContent, undefined, messageType);
      }

      this.newMessage = '';
      this.showEmoji = false;
      this.clearTypingState(); // Stop typing immediately on send
  }

  onTyping() {
      if (!this.currentConv) return;
      
      // 1. Send StartTyping if not already
      if (!this.isTyping) {
          this.isTyping = true;
          this.state.sendTyping(this.currentConv.id);
      }

      // 2. Clear existing stop timer
      if (this.typingTimeout) {
          clearTimeout(this.typingTimeout);
      }

      // 3. Set new stop timer (1s debounce)
      this.typingTimeout = setTimeout(() => {
          this.clearTypingState();
      }, 1000);
  }

  private clearTypingState() {
      if (this.isTyping && this.currentConv) {
          this.state.sendStoppedTyping(this.currentConv.id);
      }
      this.isTyping = false;
      if (this.typingTimeout) {
          clearTimeout(this.typingTimeout);
          this.typingTimeout = null;
      }
  }

  toggleAttachMenu() {
    this.showAttachMenu = !this.showAttachMenu;
    this.showEmoji = false;
  }

  handleFileSelection(event: any, type: 'image' | 'file') {
      const file = event.target.files[0];
      if (!file) return;

      this.selectedFile = file;
      this.selectedMessageType = type === 'image' ? ChatMessageTypeEnum.Image : ChatMessageTypeEnum.File;

      if (type === 'image') {
          const reader = new FileReader();
          reader.onload = (e: any) => {
              this.filePreviewUrl = e.target.result;
              this.cdr.markForCheck();
          };
          reader.readAsDataURL(file);
      } else {
          this.filePreviewUrl = null;
      }
      
      this.showAttachMenu = false;
      this.showMobileActions = false;
      
      // Reset input
      event.target.value = '';
  }
  
  clearAttachment() {
      this.selectedFile = null;
      this.filePreviewUrl = null;
      this.selectedMessageType = ChatMessageTypeEnum.Text;
  }

  openImageViewer(url: string | null | undefined) {
      if (url) this.viewingImage = url;
  }

  closeImageViewer() {
      this.viewingImage = null;
  }

  addEmoji(e: any) {
      this.newMessage += e.emoji.native;
  }

  getAvatar(conv: ConversationResponse): string | undefined {
      return (conv.participants?.[0]?.profilePictureUrl) || undefined;
  }

  getInitials(name: string): string {
      if (!name) return 'U';
      return name.charAt(0).toUpperCase();
  }

  userPresence$ = this.state.userPresence$;

  getPresenceObj(presenceMap: Map<string, { presenceStatus: number, lastSeen: string | null }> | null, conv: ConversationResponse): { presenceStatus: number, lastSeen: string | null } | undefined {
      const userId = conv.participants?.[0]?.userId;
      if (!userId || !presenceMap) return undefined;
      return presenceMap.get(userId.toLowerCase());
  }

  formatLastSeen(dateString: string | null | undefined): string {
      if (!dateString) return '';
      try {
          let dateStr = dateString;
          if (!dateStr.endsWith('Z') && !dateStr.includes('+')) {
              dateStr += 'Z';
          }
          const date = new Date(dateStr);
          return formatDistanceToNow(date, { addSuffix: true });
      } catch (e) {
          return '';
      }
  }

  goBack() {
      this.state.clearActiveConversation();
  }
  
  getFileName(url: string | null | undefined): string {
      if (!url) return 'File Attachment';
      try {
          const decodedUrl = decodeURIComponent(url);
          const parts = decodedUrl.split(/[\/\\]/);
          const lastPart = parts.pop();
          if (lastPart) {
              const nameWithoutQuery = lastPart.split('?')[0];
              return nameWithoutQuery || url; // Fallback to URL if parsing weird
          }
      } catch (e) {}
      return url || 'File Attachment';
  }

  confirmDownload(url: string | null | undefined, name: string) {
      if (!url) return;
      this.confirmDownloadTarget = { url, name };
      this.displayDownloadConfirm = true;
  }

  executeDownload() {
      if (this.confirmDownloadTarget) {
          window.open(this.confirmDownloadTarget.url, '_blank');
          this.displayDownloadConfirm = false;
          this.confirmDownloadTarget = null;
      }
  }

  cancelDownload() {
      this.displayDownloadConfirm = false;
      this.confirmDownloadTarget = null;
  }
  
  // Media Drawer Methods
  openMediaDrawer() {
      this.showMediaDrawer = true;
      this.showPinnedDrawer = false;
      this.loadMedia('File'); // Default tab
  }

  // Pinned Drawer Methods
  togglePinnedDrawer() {
      this.showPinnedDrawer = !this.showPinnedDrawer;
      if (this.showPinnedDrawer) this.showMediaDrawer = false;
  }
  
  togglePin(msg: ChatMessageResponse) {
      if (!this.currentConv) return;
      this.chatService.togglePin({
          conversationId: this.currentConv.id,
          messageId: msg.id,
          isPinned: !msg.isPinned
      }).subscribe();
  }

  loadMedia(type: 'Image' | 'File' | 'Link' | 'All', append = false) {
      if (!this.currentConv) return;
      
      if (!append) {
          this.mediaType = type;
          this.isMediaLoading = true;
          this.mediaList = [];
          this.mediaSkip = 0;
          this.hasMoreMedia = true;
      } else {
          if (this.isLoadingMoreMedia || !this.hasMoreMedia) return;
          this.isLoadingMoreMedia = true;
      }
      
      this.cdr.markForCheck();

      this.chatService.getChatMedia(this.currentConv.id, type, this.mediaSkip, this.mediaTake).subscribe({
          next: (res: any) => {
              this.isMediaLoading = false;
              this.isLoadingMoreMedia = false;
              
              if (res && res.result) {
                  const items: any[] = res.result || [];
                  items.forEach(item => {
                      if (item.sentAt && typeof item.sentAt === 'string' && !item.sentAt.endsWith('Z') && !item.sentAt.includes('+')) {
                          item.sentAt = item.sentAt + 'Z';
                      }
                  });
                  
                  if (append) {
                      this.mediaList = [...this.mediaList, ...items];
                  } else {
                      this.mediaList = items;
                  }
                  
                  this.mediaSkip += items.length;
                  this.hasMoreMedia = items.length === this.mediaTake; // If we got fewer than requested, we're at the end
              } else {
                  this.hasMoreMedia = false;
              }
              this.cdr.markForCheck();
          },
          error: () => {
              this.isMediaLoading = false;
              this.isLoadingMoreMedia = false;
              this.cdr.markForCheck();
          }
      });
  }
  
  onMediaScroll(event: any) {
      const target = event.target;
      if (!target) return;
      
      const limit = target.scrollHeight - target.clientHeight;
      if (target.scrollTop >= limit - 50) { // 50px threshold
          this.loadMedia(this.mediaType, true);
      }
  }

  showDateSeparator(msg: ChatMessageResponse, index: number, messages: ChatMessageResponse[]): boolean {
      if (index === 0) return true; // Always show for the first message
      
      const prevMsg = messages[index - 1];
      const currentDate = new Date(msg.sentAt);
      const prevDate = new Date(prevMsg.sentAt);
      
      // Check if the year, month, or day is different
      return currentDate.getFullYear() !== prevDate.getFullYear() ||
             currentDate.getMonth() !== prevDate.getMonth() ||
             currentDate.getDate() !== prevDate.getDate();
  }

  formatDateHeader(dateInput: Date | string): string {
      const date = new Date(dateInput);
      const today = new Date();
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);

      if (date.toDateString() === today.toDateString()) {
          return 'Today';
      } else if (date.toDateString() === yesterday.toDateString()) {
          return 'Yesterday';
      } else {
          return date.toLocaleDateString(undefined, {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric'
          });
      }
  }
  
  // Handle Editor Initialization
  handleEditorInit(event: any) {
    const quill = event.editor;
    
    // 1. Enter Key Binding (Native DOM Listener - Strongest Enforcer)
    // We attach directly to the root DOM node to intercept the Enter key 
    // before Quill's internal matchers can claim it as a newline.
    quill.root.addEventListener('keydown', (e: KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault(); // Stop newline insertion
            this.send();        // Send message
            return;
        }
    });

    // Backup: Standard Quill Binding (Double safety)
    quill.keyboard.addBinding({
        key: 'Enter',
        shiftKey: false
    }, (range: any, context: any) => {
        this.send();
        return false; 
    });

    // 2. Auto-Link via Text Change (Robust)
    // Watches for any text insertion to detect links immediately
    quill.on('text-change', (delta: any, oldDelta: any, source: string) => {
        if (source !== 'user') return;

        const urlRegex = /((https?:\/\/[^\s]+)|(www\.[^\s]+))/g;
        let index = 0;

        delta.ops.forEach((op: any) => {
            if (op.retain) {
                index += op.retain;
            } else if (op.insert && typeof op.insert === 'string') {
                const text = op.insert;
                let match;
                
                // Scan the inserted text for URLs
                while ((match = urlRegex.exec(text)) !== null) {
                    const url = match[0];
                    const linkUrl = url.startsWith('www.') ? 'https://' + url : url;
                    const matchIndex = index + match.index;
                    const matchLength = url.length;

                    // Apply formatting asynchronously to avoid disrupting the current change
                    setTimeout(() => {
                        quill.formatText(matchIndex, matchLength, 'link', linkUrl, 'api');
                    }, 0);
                }
                index += text.length;
            } else {
                index += 1; // Embed or newline
            }
        });
    });
  }
}
